/**
 * AuthCodeForm — the 6-digit code step of every emailed-code screen on both
 * web portals (sign-in, a new device, the authenticator, sign-up's
 * confirmation, a social sign-up that must prove its email).
 *
 * The code submits itself once all six are in; a refused one is cleared for
 * the next try; "send a new code" waits out its cooldown. Texts are passed in
 * translated, like everything this package renders.
 */

import { AlertCircle, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Alert, AlertDescription } from '../primitives/alert';
import { Button } from '../primitives/button';
import { Label } from '../primitives/label';
import { CODE_LENGTH, CodeField } from './code-field';

const RESEND_COOLDOWN_S = 60;

export interface AuthCodeFormProps {
  labels: {
    code: string;
    submit: string;
    resend: string;
    /** "You can send a new code in 42 s." */
    resendIn: (seconds: number) => string;
  };
  /** Resolves when the attempt settles (the page moves on by itself on success). */
  onSubmit: (code: string) => Promise<void>;
  /** Resolves `true` when a new code went out. Absent: no resend (an authenticator). */
  onResend?: () => Promise<boolean>;
  error: string | null;
  /** `${testIdPrefix}-code`, `-code-submit`, `-code-resend`, `-error`. */
  testIdPrefix: string;
}

export function AuthCodeForm({
  labels,
  onSubmit,
  onResend,
  error,
  testIdPrefix,
}: AuthCodeFormProps) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(onResend ? RESEND_COOLDOWN_S : 0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (error) setCode('');
  }, [error]);

  const submit = useCallback(
    async (value: string) => {
      if (value.length !== CODE_LENGTH || submitting) return;
      setSubmitting(true);
      await onSubmit(value);
      setSubmitting(false);
    },
    [onSubmit, submitting]
  );

  const resend = useCallback(async () => {
    if (onResend && (await onResend())) setCooldown(RESEND_COOLDOWN_S);
  }, [onResend]);

  const fieldId = `${testIdPrefix}-code`;

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit(code);
      }}
    >
      {error && (
        <Alert variant="destructive" data-testid={`${testIdPrefix}-error`}>
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col gap-2">
        <Label htmlFor={fieldId} className="text-sm font-medium">
          {labels.code}
        </Label>
        <CodeField
          id={fieldId}
          value={code}
          onChange={setCode}
          onComplete={(value) => void submit(value)}
          disabled={submitting}
          invalid={!!error}
          autoFocus
          data-testid={fieldId}
        />
      </div>
      <Button
        type="submit"
        className="h-11 text-base font-semibold"
        disabled={submitting || code.length !== CODE_LENGTH}
        data-testid={`${testIdPrefix}-code-submit`}
      >
        {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
        {labels.submit}
      </Button>
      {onResend && (
        <p className="text-center text-sm text-muted-foreground">
          {cooldown > 0 ? (
            labels.resendIn(cooldown)
          ) : (
            <button
              type="button"
              onClick={() => void resend()}
              className="font-semibold text-primary-accessible hover:underline"
              data-testid={`${testIdPrefix}-code-resend`}
            >
              {labels.resend}
            </button>
          )}
        </p>
      )}
    </form>
  );
}
